import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import {
  ArrowLeft, RefreshCw, Shield, Layers, Fuel, Wallet,
  CheckCircle2, XCircle, Clock, Copy, ExternalLink,
  Zap, Server, Plus, ChevronRight, Key, Ban, Sparkles,
  Users, Lock, AlertTriangle, DollarSign
} from 'lucide-react';
import { aaApi } from '../lib/aaApi';
import { BACKEND_URL, API } from '@/lib/backendUrl';

function truncateAddress(addr) {
  if (!addr) return '';
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

function StatusDot({ active }) {
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full ${active ? 'bg-emerald-500' : 'bg-red-400'}`}
      data-testid="status-dot"
    />
  );
}

function KpiCard({ label, value, icon: Icon, subtitle, testId, accent }) {
  const accentClasses = {
    teal: 'bg-[hsl(184,78%,34%)]/10 text-[hsl(184,78%,34%)]',
    brass: 'bg-[hsl(38,35%,52%)]/10 text-[hsl(38,35%,52%)]',
    emerald: 'bg-emerald-500/10 text-emerald-600',
    slate: 'bg-slate-500/10 text-slate-600',
  };

  return (
    <Card className="card-hover-lift" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
            <p className="text-2xl md:text-3xl heading-font tabular-nums">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accentClasses[accent] || accentClasses.teal}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AccountAbstractionPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [paymaster, setPaymaster] = useState(null);
  const [accounts, setAccounts] = useState({ accounts: [], total: 0 });
  const [sessionKeyStats, setSessionKeyStats] = useState(null);
  const [securityStats, setSecurityStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [newEoa, setNewEoa] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [creating, setCreating] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [configData, dashData, pmData, acctData, skStats, secStats] = await Promise.all([
        aaApi.getConfig(),
        aaApi.getDashboard(),
        aaApi.getPaymasterStatus(),
        aaApi.listAccounts(),
        aaApi.getSessionKeyStats().catch(() => null),
        aaApi.getSecurityStats().catch(() => null),
      ]);
      setConfig(configData);
      setDashboard(dashData);
      setPaymaster(pmData);
      setAccounts(acctData);
      setSessionKeyStats(skStats);
      setSecurityStats(secStats);
    } catch (err) {
      toast.error('Failed to load AA data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const handleCreateAccount = async () => {
    if (!newEoa || !newEoa.startsWith('0x') || newEoa.length !== 42) {
      toast.error('Enter a valid EOA address (0x...)');
      return;
    }
    setCreating(true);
    try {
      const result = await aaApi.createSmartAccount(newEoa, newEmail || null);
      toast.success(`Smart account created: ${truncateAddress(result.smart_account_address)}`);
      setNewEoa('');
      setNewEmail('');
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to create smart account');
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="min-h-dvh bg-background flex items-center justify-center" data-testid="aa-page-loading">
        <div className="text-center space-y-3">
          <div className="w-12 h-12 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground">Loading AA Infrastructure...</p>
        </div>
      </div>
    );
  }

  const stats = dashboard?.smart_accounts || {};
  const ops = dashboard?.user_operations || {};
  const infra = dashboard?.infrastructure || config || {};

  return (
    <TooltipProvider>
      <div className="min-h-dvh bg-background text-foreground" data-testid="aa-dashboard-page">
        {/* Header */}
        <div className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="aa-back-btn">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-lg md:text-xl heading-font tracking-tight flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[hsl(184,78%,34%)]" />
                  Account Abstraction
                </h1>
                <p className="text-xs text-muted-foreground">ERC-4337 Smart Accounts & Paymaster Infrastructure</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="hidden sm:flex items-center gap-1.5 text-xs">
                <StatusDot active={infra.rpc_connected} />
                {infra.chain_name || 'Base Sepolia'}
              </Badge>
              <Button
                variant="outline" size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                data-testid="aa-refresh-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 space-y-6">

          {/* KPI Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="aa-kpi-row">
            <KpiCard
              label="Smart Accounts"
              value={stats.total || 0}
              icon={Wallet}
              subtitle={`${stats.deployed || 0} deployed`}
              testId="kpi-smart-accounts"
              accent="teal"
            />
            <KpiCard
              label="UserOperations"
              value={ops.total || 0}
              icon={Layers}
              subtitle={`${ops.confirmed || 0} confirmed`}
              testId="kpi-user-ops"
              accent="brass"
            />
            <KpiCard
              label="Paymaster"
              value={paymaster?.configured ? 'Active' : 'Inactive'}
              icon={Fuel}
              subtitle={paymaster?.provider === 'pimlico' ? 'Pimlico' : 'Not configured'}
              testId="kpi-paymaster"
              accent={paymaster?.configured ? 'emerald' : 'slate'}
            />
            <KpiCard
              label="EntryPoint"
              value={infra.entry_point_version || 'v0.7'}
              icon={Server}
              subtitle={`Block ${infra.current_block?.toLocaleString() || 'N/A'}`}
              testId="kpi-entrypoint"
              accent="teal"
            />
          </div>

          {/* Tabs */}
          <Tabs defaultValue="infrastructure" className="space-y-4">
            <TabsList data-testid="aa-tabs">
              <TabsTrigger value="infrastructure" data-testid="aa-tab-infra">Infrastructure</TabsTrigger>
              <TabsTrigger value="accounts" data-testid="aa-tab-accounts">Smart Accounts</TabsTrigger>
              <TabsTrigger value="operations" data-testid="aa-tab-ops">UserOperations</TabsTrigger>
              <TabsTrigger value="session-keys" data-testid="aa-tab-session-keys">Session Keys</TabsTrigger>
              <TabsTrigger value="security" data-testid="aa-tab-security">Security</TabsTrigger>
              <TabsTrigger value="paymaster" data-testid="aa-tab-paymaster">Paymaster</TabsTrigger>
            </TabsList>

            {/* Infrastructure Tab */}
            <TabsContent value="infrastructure" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* ERC-4337 Stack */}
                <Card data-testid="aa-stack-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base heading-font flex items-center gap-2">
                      <Layers className="w-4 h-4 text-[hsl(184,78%,34%)]" />
                      ERC-4337 Stack
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <InfraRow
                      label="EntryPoint Contract"
                      value={infra.entry_point}
                      version={infra.entry_point_version}
                      onCopy={() => copyToClipboard(infra.entry_point)}
                    />
                    <Separator />
                    <InfraRow
                      label="SimpleAccount Factory"
                      value={infra.factory_address}
                      onCopy={() => copyToClipboard(infra.factory_address)}
                    />
                    <Separator />
                    <InfraRow
                      label="Account Type"
                      value={infra.account_type || 'SimpleAccount'}
                      plain
                    />
                    <Separator />
                    <InfraRow
                      label="Chain"
                      value={`${infra.chain_name} (${infra.chain_id})`}
                      plain
                    />
                  </CardContent>
                </Card>

                {/* Network Status */}
                <Card data-testid="aa-network-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base heading-font flex items-center gap-2">
                      <Server className="w-4 h-4 text-[hsl(184,78%,34%)]" />
                      Network & Bundler
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">RPC Connection</span>
                      <Badge variant={infra.rpc_connected ? 'default' : 'destructive'} className="text-xs" data-testid="rpc-status-badge">
                        {infra.rpc_connected ? (
                          <><CheckCircle2 className="w-3 h-3 mr-1" /> Connected</>
                        ) : (
                          <><XCircle className="w-3 h-3 mr-1" /> Disconnected</>
                        )}
                      </Badge>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Bundler Provider</span>
                      <Badge
                        variant={infra.bundler_configured ? 'default' : 'outline'}
                        className="text-xs"
                        data-testid="bundler-status-badge"
                      >
                        {infra.bundler_configured ? (
                          <><Zap className="w-3 h-3 mr-1" /> {infra.bundler_provider}</>
                        ) : (
                          <><Clock className="w-3 h-3 mr-1" /> Not configured</>
                        )}
                      </Badge>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Current Block</span>
                      <span className="text-sm mono tabular-nums" data-testid="current-block">
                        {infra.current_block?.toLocaleString() || 'N/A'}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Paymaster Type</span>
                      <span className="text-sm capitalize">{infra.paymaster_type || 'none'}</span>
                    </div>

                    {!infra.bundler_configured && (
                      <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <p className="text-xs text-amber-700">
                          Set <span className="mono">PIMLICO_API_KEY</span> in backend .env to enable
                          gasless transaction sponsorship via Pimlico bundler.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Architecture Overview */}
              <Card data-testid="aa-architecture-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base heading-font">How It Works</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <StepCard
                      step="1"
                      title="Guest Connects Wallet"
                      description="Guest logs in via email/social (Reown AppKit). An EOA is provisioned. A Smart Account is computed (counterfactual)."
                    />
                    <StepCard
                      step="2"
                      title="Build UserOperation"
                      description="Backend constructs a UserOp for the transaction (e.g., booking). Gas is estimated and Paymaster data is attached."
                    />
                    <StepCard
                      step="3"
                      title="Gasless Execution"
                      description="Guest signs the UserOp. Bundler submits it on-chain. Paymaster sponsors gas. Guest pays zero gas fees."
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Smart Accounts Tab */}
            <TabsContent value="accounts" className="space-y-4">
              {/* Create Smart Account */}
              <Card data-testid="aa-create-account-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base heading-font flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Create Smart Account
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Input
                      placeholder="EOA Address (0x...)"
                      value={newEoa}
                      onChange={(e) => setNewEoa(e.target.value)}
                      className="mono text-sm flex-1"
                      data-testid="aa-eoa-input"
                    />
                    <Input
                      placeholder="Guest email (optional)"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      className="text-sm sm:w-48"
                      data-testid="aa-email-input"
                    />
                    <Button
                      onClick={handleCreateAccount}
                      disabled={creating || !newEoa}
                      data-testid="aa-create-btn"
                    >
                      {creating ? 'Creating...' : 'Create'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Accounts List */}
              <Card data-testid="aa-accounts-list-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base heading-font flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Wallet className="w-4 h-4" />
                      Smart Accounts ({accounts.total})
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {accounts.accounts.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground" data-testid="aa-no-accounts">
                      <Wallet className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">No smart accounts yet</p>
                      <p className="text-xs mt-1">Create one above or connect a guest wallet</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">EOA Owner</TableHead>
                          <TableHead className="text-xs">Smart Account</TableHead>
                          <TableHead className="text-xs">Email</TableHead>
                          <TableHead className="text-xs">Status</TableHead>
                          <TableHead className="text-xs text-right">Txns</TableHead>
                          <TableHead className="text-xs">Created</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {accounts.accounts.map((acct, i) => (
                          <TableRow key={i} data-testid={`aa-account-row-${i}`}>
                            <TableCell className="mono text-xs">
                              <div className="flex items-center gap-1">
                                <span>{truncateAddress(acct.eoa_address)}</span>
                                <button
                                  onClick={() => copyToClipboard(acct.eoa_address)}
                                  className="text-muted-foreground hover:text-foreground transition-colors"
                                  data-testid={`copy-eoa-${i}`}
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                            </TableCell>
                            <TableCell className="mono text-xs">
                              <div className="flex items-center gap-1">
                                <span>{truncateAddress(acct.smart_account_address)}</span>
                                <button
                                  onClick={() => copyToClipboard(acct.smart_account_address)}
                                  className="text-muted-foreground hover:text-foreground transition-colors"
                                  data-testid={`copy-sa-${i}`}
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <a
                                      href={`https://sepolia.basescan.org/address/${acct.smart_account_address}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                    </a>
                                  </TooltipTrigger>
                                  <TooltipContent>View on BaseScan</TooltipContent>
                                </Tooltip>
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {acct.guest_email || '-'}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={acct.is_deployed ? 'default' : 'outline'}
                                className="text-xs"
                                data-testid={`status-badge-${i}`}
                              >
                                {acct.is_deployed ? 'Deployed' : 'Counterfactual'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-right tabular-nums">
                              {acct.tx_count || 0}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {acct.created_at ? new Date(acct.created_at).toLocaleDateString() : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* UserOperations Tab */}
            <TabsContent value="operations" className="space-y-4">
              <Card data-testid="aa-ops-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base heading-font flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    Recent UserOperations
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {(dashboard?.recent_operations || []).length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground" data-testid="aa-no-ops">
                      <Layers className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">No UserOperations submitted yet</p>
                      <p className="text-xs mt-1">Operations will appear here when guests execute gasless transactions</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">UserOp Hash</TableHead>
                          <TableHead className="text-xs">Sender</TableHead>
                          <TableHead className="text-xs">Status</TableHead>
                          <TableHead className="text-xs">Tx Hash</TableHead>
                          <TableHead className="text-xs">Submitted</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(dashboard?.recent_operations || []).map((op, i) => (
                          <TableRow key={i} data-testid={`aa-op-row-${i}`}>
                            <TableCell className="mono text-xs">
                              {truncateAddress(op.user_op_hash)}
                            </TableCell>
                            <TableCell className="mono text-xs">
                              {truncateAddress(op.sender)}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={op.status === 'confirmed' ? 'default' : op.status === 'failed' ? 'destructive' : 'outline'}
                                className="text-xs"
                              >
                                {op.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="mono text-xs">
                              {op.tx_hash ? (
                                <a
                                  href={`https://sepolia.basescan.org/tx/${op.tx_hash}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline flex items-center gap-1"
                                >
                                  {truncateAddress(op.tx_hash)}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {op.submitted_at ? new Date(op.submitted_at).toLocaleString() : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Operations Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MiniStat label="Total" value={ops.total || 0} icon={Layers} testId="ops-total" />
                <MiniStat label="Confirmed" value={ops.confirmed || 0} icon={CheckCircle2} testId="ops-confirmed" color="text-emerald-600" />
                <MiniStat label="Pending" value={ops.pending || 0} icon={Clock} testId="ops-pending" color="text-amber-600" />
                <MiniStat label="Failed" value={ops.failed || 0} icon={XCircle} testId="ops-failed" color="text-red-500" />
              </div>
            </TabsContent>

            {/* Session Keys Tab */}
            <TabsContent value="session-keys" className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MiniStat label="Total Keys" value={sessionKeyStats?.total || 0} icon={Key} testId="sk-total" />
                <MiniStat label="Active" value={sessionKeyStats?.active || 0} icon={CheckCircle2} testId="sk-active" color="text-emerald-600" />
                <MiniStat label="Used" value={sessionKeyStats?.exhausted || 0} icon={Zap} testId="sk-used" color="text-primary" />
                <MiniStat label="Auto NFT Upgrades" value={sessionKeyStats?.auto_nft_upgrades || 0} icon={Sparkles} testId="sk-upgrades" color="text-amber-600" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card data-testid="sk-how-it-works">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base heading-font flex items-center gap-2">
                      <Key className="w-4 h-4 text-[hsl(184,78%,34%)]" />
                      How Session Keys Work
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <StepCard
                      step="1"
                      title="Guest Grants Permission"
                      description="After a gasless booking, the guest grants a session key allowing the protocol to auto-upgrade their NFT at checkout."
                    />
                    <StepCard
                      step="2"
                      title="Checkout Triggers"
                      description="When the hotel checks out the guest, the system finds the active session key and executes the NFT conversion automatically."
                    />
                    <StepCard
                      step="3"
                      title="NFT Becomes Collectible"
                      description="The booking NFT transforms into a Stay Collectible with AI-generated art. No guest action required."
                    />
                  </CardContent>
                </Card>

                <Card data-testid="sk-permissions">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base heading-font flex items-center gap-2">
                      <Shield className="w-4 h-4 text-[hsl(184,78%,34%)]" />
                      Permission Types
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="p-3 rounded-lg border bg-card">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm heading-font">nft_upgrade</span>
                        <Badge variant="outline" className="text-[10px] text-emerald-600">Low Risk</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Allow protocol to convert booking NFT to stay collectible at checkout.
                      </p>
                    </div>
                    <div className="p-3 rounded-lg border bg-card">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm heading-font">token_reward</span>
                        <Badge variant="outline" className="text-[10px] text-emerald-600">Low Risk</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Allow protocol to claim $HERMES loyalty rewards on guest's behalf.
                      </p>
                    </div>
                    <div className="p-3 rounded-lg border bg-muted/50">
                      <p className="text-xs text-muted-foreground">
                        Session keys are scoped, time-limited, and revocable by the guest at any time.
                        They never allow fund transfers or account changes.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Security Tab - Phase 4 */}
            <TabsContent value="security" className="space-y-4">
              {/* Security Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MiniStat
                  label="Guardians Active"
                  value={securityStats?.guardians?.total_active || 0}
                  icon={Users} testId="sec-guardians" color="text-primary"
                />
                <MiniStat
                  label="Accounts Protected"
                  value={securityStats?.guardians?.accounts_protected || 0}
                  icon={Shield} testId="sec-protected" color="text-emerald-600"
                />
                <MiniStat
                  label="Pending Recoveries"
                  value={securityStats?.recoveries?.pending || 0}
                  icon={AlertTriangle} testId="sec-pending-rec" color="text-amber-600"
                />
                <MiniStat
                  label="Spending Configs"
                  value={securityStats?.spending_limits?.accounts_configured || 0}
                  icon={DollarSign} testId="sec-spending" color="text-primary"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Social Recovery Card */}
                <Card data-testid="sec-recovery-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base heading-font flex items-center gap-2">
                      <Users className="w-4 h-4 text-[hsl(184,78%,34%)]" />
                      Social Recovery
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <StepCard
                      step="1"
                      title="Designate Guardians"
                      description="Add 2-5 trusted guardians (friends, family, or the protocol itself) to protect your smart account."
                    />
                    <StepCard
                      step="2"
                      title="Recovery Vote"
                      description="If you lose access, guardians vote to approve ownership transfer. Requires 2-of-N threshold."
                    />
                    <StepCard
                      step="3"
                      title="48h Timelock"
                      description="After approval, there's a 48-hour delay before execution. You can cancel during this window."
                    />
                    <div className="p-3 rounded-lg border bg-card">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm heading-font">Guardian Types</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <Badge variant="outline" className="text-[10px]">Trusted Contact</Badge>
                        <Badge variant="outline" className="text-[10px]">Hotel Protocol</Badge>
                        <Badge variant="outline" className="text-[10px]">Hardware Wallet</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Spending Limits Card */}
                <Card data-testid="sec-spending-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base heading-font flex items-center gap-2">
                      <Lock className="w-4 h-4 text-[hsl(184,78%,34%)]" />
                      Spending Limits
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="p-3 rounded-lg border bg-card space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Daily Default</span>
                        <span className="text-sm heading-font mono">$500.00</span>
                      </div>
                      <Separator />
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Monthly Default</span>
                        <span className="text-sm heading-font mono">$5,000.00</span>
                      </div>
                    </div>
                    <div className="p-3 rounded-lg border bg-card space-y-2">
                      <p className="text-xs heading-font">How Limits Protect You</p>
                      <ul className="space-y-1.5">
                        <li className="flex items-start gap-2 text-xs text-muted-foreground">
                          <CheckCircle2 className="w-3 h-3 mt-0.5 text-emerald-600 shrink-0" />
                          <span>Every transaction is checked against daily and monthly caps</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-muted-foreground">
                          <CheckCircle2 className="w-3 h-3 mt-0.5 text-emerald-600 shrink-0" />
                          <span>Limits are owner-configurable and reset automatically</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-muted-foreground">
                          <CheckCircle2 className="w-3 h-3 mt-0.5 text-emerald-600 shrink-0" />
                          <span>Spending history tracked with real-time utilization %</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-muted-foreground">
                          <CheckCircle2 className="w-3 h-3 mt-0.5 text-emerald-600 shrink-0" />
                          <span>Prevents unauthorized large transactions even if session keys leak</span>
                        </li>
                      </ul>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50 border">
                      <p className="text-xs text-muted-foreground">
                        Spending limits are enforced before any UserOperation is submitted.
                        Guests can customize their limits via the Guest Portal or API.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Paymaster Tab */}
            <TabsContent value="paymaster" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card data-testid="aa-paymaster-config-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base heading-font flex items-center gap-2">
                      <Fuel className="w-4 h-4 text-emerald-600" />
                      Paymaster Configuration
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Provider</span>
                      <span className="text-sm heading-font capitalize">
                        {paymaster?.provider || 'None'}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Sponsorship</span>
                      <Badge
                        variant={paymaster?.sponsorship_active ? 'default' : 'outline'}
                        className="text-xs"
                        data-testid="sponsorship-badge"
                      >
                        {paymaster?.sponsorship_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Sponsored Ops</span>
                      <span className="text-sm tabular-nums">{paymaster?.total_sponsored_ops || 0}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Confirmed Ops</span>
                      <span className="text-sm tabular-nums">{paymaster?.confirmed_ops || 0}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="aa-paymaster-guide-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base heading-font flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-600" />
                      Setup Guide
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <SetupStep
                      number="1"
                      title="Get Pimlico API Key"
                      description="Sign up at dashboard.pimlico.io and create an API key for Base Sepolia."
                      done={paymaster?.configured}
                    />
                    <SetupStep
                      number="2"
                      title="Configure Backend"
                      description="Set PIMLICO_API_KEY in backend/.env and restart the backend service."
                      done={paymaster?.configured}
                    />
                    <SetupStep
                      number="3"
                      title="Fund Paymaster"
                      description="Deposit ETH to sponsor gas for guest transactions on Base Sepolia."
                      done={false}
                    />
                    <SetupStep
                      number="4"
                      title="Wire to Booking Flow"
                      description="Phase 2: Connect AA to the booking flow for one-click gasless bookings."
                      done={false}
                    />
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </TooltipProvider>
  );
}

/* Sub-components */

function InfraRow({ label, value, version, plain, onCopy }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm text-muted-foreground shrink-0">{label}</span>
      <div className="flex items-center gap-1.5 min-w-0">
        {version && (
          <Badge variant="outline" className="text-xs shrink-0">{version}</Badge>
        )}
        {plain ? (
          <span className="text-sm truncate">{value}</span>
        ) : (
          <>
            <span className="mono text-xs truncate">{truncateAddress(value)}</span>
            {onCopy && (
              <button
                onClick={onCopy}
                className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                data-testid={`copy-${label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <Copy className="w-3 h-3" />
              </button>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={`https://sepolia.basescan.org/address/${value}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                >
                  <ExternalLink className="w-3 h-3" />
                </a>
              </TooltipTrigger>
              <TooltipContent>View on BaseScan</TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
}

function StepCard({ step, title, description }) {
  return (
    <div className="p-4 rounded-lg border bg-card space-y-2" data-testid={`aa-step-${step}`}>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-[hsl(184,78%,34%)]/10 text-[hsl(184,78%,34%)] flex items-center justify-center text-xs heading-font">
          {step}
        </div>
        <ChevronRight className="w-3 h-3 text-muted-foreground" />
        <h3 className="text-sm heading-font">{title}</h3>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
}

function MiniStat({ label, value, icon: Icon, testId, color }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4 flex items-center gap-3">
        <Icon className={`w-4 h-4 ${color || 'text-muted-foreground'}`} />
        <div>
          <p className="text-lg heading-font tabular-nums">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function SetupStep({ number, title, description, done }) {
  return (
    <div className="flex items-start gap-3" data-testid={`setup-step-${number}`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs heading-font ${
        done
          ? 'bg-emerald-500/10 text-emerald-600'
          : 'bg-muted text-muted-foreground'
      }`}>
        {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : number}
      </div>
      <div>
        <p className={`text-sm heading-font ${done ? 'text-emerald-600' : ''}`}>{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
    </div>
  );
}
